import { useEffect, useRef, useState, type ReactNode } from "react"
import type { MascotState, StepKind, StepStatus } from "../contract"
import { Beam, beamStateFor } from "../ds"
import type { TimedStep } from "./Loader"

/** How long a turn usually takes. Live Kimi runs on the dev corpus took 110 to 156 s (Oct 2026). */
export const USUAL = "Usually about two minutes."
/** No new step or section for this long and the reader is told, with what is kept. */
export const STALL_MS = 45_000

interface Stage { key: string; label: string; detail?: string; status: StepStatus; t?: number }

const NUM = ["no", "one", "two", "three", "four", "five", "six"]
const count = (n: number) => NUM[n] ?? String(n)

/** Fold the live step log into the six staged lines of Working-v4. */
export function stagesOf(steps: TimedStep[], finished: boolean): Stage[] {
  const of = (...kinds: StepKind[]) => steps.filter((s) => kinds.includes(s.kind))
  const status = (group: TimedStep[], later: TimedStep[]): StepStatus => {
    if (!group.length) return "pend"
    if (group.some((s) => s.status === "run") && !finished) return "run"
    if (group.every((s) => s.status === "error")) return "error"
    return later.length || finished || group.every((s) => s.status === "done" || s.status === "error") ? "done" : "run"
  }
  const first = (group: TimedStep[]) => (group.length ? Math.min(...group.map((s) => s.t)) : undefined)
  const read = of("read")
  const docs = of("doc")
  const limit = of("limit")
  const search = of("search")
  const found = of("found", "read_source")
  const write = of("verify", "write")
  const decisions = of("found").reduce((n, s) => n + (Number(/(\d+)/.exec(s.label)?.[1]) || 0), 0)
  const reading = of("read_source").length
  const stages: Stage[] = [
    { key: "read", label: "Reading the question", status: status(read, [...docs, ...limit, ...search]), t: first(read) },
  ]
  if (docs.length) stages.push({ key: "docs", label: "Reading your documents", status: status(docs, [...limit, ...search]), t: first(docs) })
  if (limit.length) stages.push({ key: "limit", label: "Working out the time limit", status: status(limit, [...search, ...found]), t: first(limit) })
  stages.push(
    {
      key: "search",
      label: search.length > 1 ? `Searching ${count(search.length)} ways` : "Searching the decisions",
      status: status(search, found),
      t: first(search),
    },
    {
      key: "found",
      label: decisions ? (reading ? `${decisions} decisions, reading ${reading}` : `${decisions} decisions found`) : "Reading the decisions that fit",
      status: status(found, write),
      t: first(found),
    },
    { key: "write", label: "Writing the answer", status: status(write, []), t: first(write) },
  )
  return stages
}

const Check = <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3 3 7-7" /></svg>

function Mark({ status }: { status: StepStatus }) {
  if (status === "done") return <span className="wk-mark done">{Check}</span>
  if (status === "run") return <span className="wk-mark run" role="img" aria-label="in progress" />
  if (status === "error") return <span className="wk-mark err" role="img" aria-label="did not finish" />
  return <span className="wk-mark pend" role="img" aria-label="pending" />
}

interface WorkingProps {
  id: string
  steps: TimedStep[]
  mascot: MascotState
  /** The matter on one mono line once Nira has read it. */
  matter: string
  /** Changes whenever anything new arrives (a step or a section); quiet for STALL_MS reads as a stall. */
  progress: string
  /** Sits directly under the step list (the workspace line). */
  after?: ReactNode
}

/** Working (Working-v4): the beam at work over six staged lines, honest about time and stalls. */
export function Working({ id, steps, mascot, matter, progress, after }: WorkingProps) {
  const finished = mascot === "done"
  const stages = stagesOf(steps, finished)
  const stalled = useStall(progress, !finished)
  return (
    <article className="wk" id={id} aria-label="Nira is working">
      <Beam state={beamStateFor(mascot)} size="hero" className="wk-beam" />
      <div className="wk-head">
        <h2>{finished ? "Weighed." : "Weighing your question."}</h2>
        {matter ? <p className="wk-matter">{matter}</p> : null}
      </div>
      <ol className="wk-steps" aria-live="polite">
        {stages.map((s) => (
          <li key={s.key} className={`wk-step ${s.status}`}>
            <span className="wk-t">{s.t === undefined || s.status === "pend" ? "" : `${s.t.toFixed(1)}s`}</span>
            <Mark status={s.status} />
            <span className="wk-s"><span className="wk-l1">{s.label}</span></span>
          </li>
        ))}
      </ol>
      {after}
      <p className="wk-note" aria-live="polite">
        {stalled ? "Taking longer than usual. Sections so far are kept." : `${USUAL} Start reading as sections land.`}
      </p>
    </article>
  )
}

/** True once `progress` has not changed for STALL_MS while `live`. */
function useStall(progress: string, live: boolean): boolean {
  const [stalled, setStalled] = useState(false)
  const last = useRef(Date.now())
  useEffect(() => {
    last.current = Date.now()
    setStalled(false)
  }, [progress])
  useEffect(() => {
    if (!live) return
    const t = window.setInterval(() => setStalled(Date.now() - last.current >= STALL_MS), 1000)
    return () => window.clearInterval(t)
  }, [live])
  return stalled && live
}
